import React, { useEffect, useMemo } from "react";
import { APIProvider, Map, Marker } from "@vis.gl/react-google-maps";
import { KeyRound, MapPin } from "lucide-react";
import { MAPS_KEY } from "../lib/api";

const LONDON_CENTER = { lat: 51.5072, lng: -0.1276 };

export const MapPanel = ({ markers = [], height = 360, title = "Live Map" }) => {
  const normalizedMarkers = useMemo(
    () =>
      markers
        .map((marker) => ({
          id: marker.id || marker.trip_id || `${marker.latitude}-${marker.longitude}`,
          lat: Number(marker.lat ?? marker.latitude),
          lng: Number(marker.lng ?? marker.longitude),
          label: marker.label || marker.child_name || marker.vehicle_registration || marker.title || "Tracked vehicle",
        }))
        .filter((marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng)),
    [markers]
  );

  const center = normalizedMarkers[0] ? { lat: normalizedMarkers[0].lat, lng: normalizedMarkers[0].lng } : LONDON_CENTER;

  useEffect(() => {
    if (!MAPS_KEY && process.env.NODE_ENV !== "production") {
      console.warn("Google Maps is not configured. CareFleet is using the fallback location panel until REACT_APP_GOOGLE_MAPS_API_KEY is added.");
    }
  }, []);


  if (!normalizedMarkers.length) {
    return (
      <div className="rounded-3xl border border-dashed border-[#CBD5E1] bg-white p-8 text-center text-[#64748B]" style={{ minHeight: height }}>
        <MapPin size={28} className="mx-auto mb-3 text-[#94A3B8]" />
        <p className="font-medium text-[#0F172A]">{title}</p>
        <p className="mt-2 text-sm">Live location markers will appear here as soon as a trip begins sharing GPS updates.</p>
      </div>
    );
  }

  if (!MAPS_KEY) {
    return (
      <div className="rounded-3xl border border-[#E2E8F0] bg-white p-6" style={{ minHeight: height }}>
        <div className="mb-4 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
          <KeyRound size={18} />
          <div>
            <p className="font-semibold">Google Maps key missing</p>
            <p className="text-sm">Add REACT_APP_GOOGLE_MAPS_API_KEY to enable the interactive map. GPS tracking data is still being captured and shown below.</p>
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {normalizedMarkers.map((marker) => (
            <div key={marker.id} className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
              <p className="font-medium text-[#0F172A]">{marker.label}</p>
              <p className="mt-1 text-sm text-[#64748B]">Lat {marker.lat.toFixed(5)} • Lng {marker.lng.toFixed(5)}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-[#E2E8F0] bg-white shadow-sm" style={{ height }}>
      <APIProvider apiKey={MAPS_KEY}>
        <Map
          style={{ width: "100%", height: "100%" }}
          defaultCenter={center}
          defaultZoom={normalizedMarkers.length > 1 ? 11 : 13}
          gestureHandling="greedy"
          disableDefaultUI={true}
          zoomControl
        >
          {normalizedMarkers.map((marker) => (
            <Marker key={marker.id} position={{ lat: marker.lat, lng: marker.lng }} title={marker.label} />
          ))}
        </Map>
      </APIProvider>
    </div>
  );
};

export default MapPanel;
